"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { AppContext } from "@/App"
import { Button } from "@/Components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Textarea } from "@/Components/ui/textarea"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import DatePicker from "@/Components/ui/DatePicker"

import { SupportRequest } from "@/Components/SupportRequests/SupportRequest";
import React from "react"
import { Checkbox } from "@/Components/ui/checkbox"
import { useIsMobile } from "@/hooks/use-mobile";

const formSchema = z.object({
    description: z
        .string()
        .min(1, {
            message: "Request must be at least 1 character.",
        })
        .max(250, {
            message: "Request must not be longer than 250 characters.",
        }),
    raisedBy: z.string(),
    owner: z.string(),
    workstream: z
        .string({
            required_error: "Please select a Workstream",
        }),
    workPackage: z.string({
        required_error: "Please select a work package",
    }),
    requestType: z.enum(["Resource", "Financial", "Other"]),
    rating: z.enum(["Low", "Medium", "High"]),
    status: z.enum(["Cancelled", "Complete", "Open", "Closed"]),
    date: z.date(),
    dueDate: z.date(),
    notes: z.string().max(500),
    displayOnDashboard: z.boolean(),
    displayOnStatusReport: z.boolean()
})

interface SupportRequestFormProps {
    supportRequest?: SupportRequest,
    saveSupportRequest: (supportRequest: SupportRequest) => void,
}

function SupportRequestForm({ supportRequest, saveSupportRequest }: SupportRequestFormProps) {
    const app = React.useContext(AppContext);
    const teamOptions = app.projectManagers?.concat(app.teamMembers).concat(app.sponsors) || [];
    const isMobile = useIsMobile();

    const [notesLength, setNotesLength] = React.useState(supportRequest ? supportRequest.notes?.length ?? 0 : 0);
    const [descriptionLength, setDescriptionLength] = React.useState(supportRequest ? supportRequest.description?.length ?? 0 : 0);

    // 1. Define your form.
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            workstream: supportRequest ? supportRequest.workstream : (app.selectedProject?.projectTypeId === 1 ? app.workPackageOptions?.[0]?.workstreamName : ""),
            workPackage: supportRequest ? supportRequest.workPackage : "",
            raisedBy: supportRequest ? supportRequest.raisedBy : "",
            owner: supportRequest ? supportRequest.userId === "" ? "unassigned" : supportRequest.userId : "unassigned",
            requestType: supportRequest ? supportRequest.requestType as "Resource" | "Financial" | "Other" : "Resource",
            rating: supportRequest ? (supportRequest.rating as "Low" | "Medium" | "High") : "Low",
            description: supportRequest ? supportRequest.description : "",
            date: supportRequest ? new Date(supportRequest.date) : new Date(),
            dueDate: supportRequest ? new Date(supportRequest.dueDate) : new Date(new Date().setDate(new Date().getDate() + 7)),
            status: supportRequest ? supportRequest.status : "Open",
            notes: supportRequest ? supportRequest.notes : "",
            displayOnDashboard: supportRequest ? supportRequest.displayOnDashboard : false,
            displayOnStatusReport: supportRequest ? supportRequest.displayOnStatusReport : false
        },
        mode: "onBlur",
    })

    // 2. Define a submit handler.
    function onSubmit(data: z.infer<typeof formSchema>) {
        const newSupportRequest: SupportRequest = {
            projectId: app.selectedProject?.id || 0,
            supportRequestId: supportRequest ? supportRequest.supportRequestId : 0,
            workstream: data.workstream,
            workstreamId: app.workPackageOptions?.find((ws) => ws.workstreamName === data.workstream)?.workstreamId || 0,
            workPackageId: app.workPackageOptions?.find((ws) => ws.workstreamName === data.workstream)?.workPackageNames
                .find((wp) => wp.workPackageName === data.workPackage)?.workPackageId || 0,
            workPackage: data.workPackage,
            description: data.description,
            raisedBy: data.raisedBy,
            owner: teamOptions.find(t => t.id == data.owner)?.displayName ?? "Unassigned",
            userId: data.owner == "unassigned" ? "" : data.owner,
            requestType: data.requestType,
            rating: data.rating,
            date: data.date,
            dueDate: data.dueDate,
            status: data ? data.status as "Cancelled" | "Complete" | "Open" | "Closed" : "Open",
            notes: data.notes,
            displayOnDashboard: data.displayOnDashboard,
            displayOnStatusReport: data.displayOnStatusReport,
            seen: false,
            acknowledged: false,
        }

        saveSupportRequest(newSupportRequest);

        if (supportRequest == null || supportRequest == undefined) {
            form.setValue("description", "");
            form.setValue("notes", "");

            setTimeout(() => form.setFocus("description"), 0);
        }
    }

    if (isMobile) {
        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4">
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="workPackage"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Work Package</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[425px] p-2">
                                                <SelectValue placeholder="Select a work package" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                                <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Status</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[200px] p-2">
                                                <SelectValue placeholder="Select a status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="requestType"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Request Type</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[425px] p-2">
                                                <SelectValue placeholder="Select a request type" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Resource">Resource</SelectItem>
                                            <SelectItem value="Financial">Financial</SelectItem>
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Rating</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-[200px] p-2">
                                                <SelectValue placeholder="Select a rating" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="text-2xl  w-[425px] p-2">
                                                    <SelectValue placeholder="Who raised the action..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Date Raised</FormLabel>
                                    <FormControl>
                                        <DatePicker className="text-2xl w-[200px] p-2" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Owner</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="text-2xl w-[425px] p-2">
                                                    <SelectValue placeholder="Owner..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                                <SelectItem key="unassigned" value="unassigned">Unassigned</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="dueDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-2xl">Due Date</FormLabel>
                                    <FormControl>
                                        <DatePicker className="text-2xl w-[200px] p-2" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Request</div>
                                    <div><span className="text-xl flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your support request..."
                                        className="resize-none text-2xl"
                                        rows={3}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Notes</div>
                                    <div><span className="text-xl flex text-gray-500 items-end pr-2">{notesLength}/500</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="resize-none text-2xl"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row justify-between">
                        <FormField
                            control={form.control}
                            name="displayOnDashboard"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                                    <FormControl className="scale-150 origin-left mr-2">
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormLabel className="text-2xl !mt-0">Display On Dashboard</FormLabel>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="displayOnStatusReport"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                                    <FormControl className="scale-150 origin-left mr-2">
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormLabel className="text-2xl !mt-0">Display on Status Report</FormLabel>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <Button type="submit" className="w-full text-2xl py-6">Submit</Button>
                </form>
            </Form>
        )
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="workPackage"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Work Package</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a work package" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                                <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Who raised the action..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Date Raised</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="requestType"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Request Type</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a request type" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Resource">Resource</SelectItem>
                                            <SelectItem value="Financial">Financial</SelectItem>
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="owner"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Owner</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Owner..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                                <SelectItem key="unassigned" value="unassigned">Unassigned</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="dueDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Due Date</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="rating"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Rating</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a rating" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Low">Low</SelectItem>
                                            <SelectItem value="Medium">Medium</SelectItem>
                                            <SelectItem value="High">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Request</div><div><span className="text-xs flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your support request..."
                                        className="resize-none"
                                        rows={3}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Notes </div><div><span className="text-xs flex text-gray-500 items-end pr-2">{notesLength}/500</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="resize-none"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row ml-auto space-x-4">
                        <FormField
                            control={form.control}
                            name="displayOnDashboard"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Display On Dashboard</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="displayOnStatusReport"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Display on Status Report</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button type="submit" className="w-48">Submit</Button>
                    </div>
                </div>
            </form>
        </Form>
    )
}

export default SupportRequestForm;
